import type { DomainConflictCode } from '../../../../types/models/DomainTransferability';

// Transfer-wizard-specific state helpers. The registrant-contact helpers (`ContactDraft`,
// `EMPTY_CONTACT`, `validateContactDraft`, `isContactDraftValid`, `buildContactRequest`,
// `vatRateLabel`) and the `ContactMode` type are REUSED verbatim from the registration wizard
// (`../Register/types`) — import them from there. This file holds only what differs for transfer:
// its own step ids, the cart item shape, and the transferability/create-item conflict copy.

/**
 * Stable WizardStep ids for the transfer wizard — its own set (the registration wizard's `STEP_IDS`
 * are `reg-`prefixed and include a `nameservers` step transfer doesn't have). Shared between the
 * `<Wizard>` step definitions and the create-error step-jump logic.
 */
export const TRANSFER_STEP_IDS = {
  domains: 'transfer-domains',
  contact: 'transfer-contact',
  recap: 'transfer-recap',
} as const;

/** The most domains that may be transferred in a single order (backend cap). */
export const MAX_TRANSFER_CART = 25;

/**
 * One line in the transfer cart. `authCode` is the secret transfer/EPP code, held only in memory
 * (page-level state) — masked in the cart UI and never put in a URL/log/notification; it travels
 * only in the create-order body.
 */
export interface TransferCartItem {
  /** The FQDN to transfer in (normalized, lowercased). */
  domain: string;
  tld: string;
  /** OUR transfer sell price excl. VAT (`DECIMAL(10,4)` string), or `null` if unpriced. */
  price: string | null;
  authCode: string;
}

// A lenient FQDN check for the step-1 input: at least one dot and a plausible label structure. The
// backend re-validates + normalizes; this just gates the Check button so we don't fire on garbage.
const FQDN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

/** True when the trimmed value looks like a full domain name (has a dot + a plausible structure). */
export function isLikelyFqdn(value: string): boolean {
  return FQDN_RE.test(value.trim());
}

/** Normalize a domain the same way the backend does (lowercase, trim, drop a single trailing dot). */
export function normalizeDomainInput(value: string): string {
  return value.trim().toLowerCase().replace(/\.$/, '');
}

// Friendly, human copy for the transferability `conflict` codes (why a name can't be transferred in).
const CONFLICT_COPY: Record<DomainConflictCode, string> = {
  domain_owned_by_another_organization: 'This domain already belongs to another organization on Ancalagon.',
  domain_already_registered_here: "This domain is already registered with us — there's nothing to transfer in.",
  domain_already_in_transfer: 'A transfer for this domain is already in progress.',
  domain_reserved_by_another_organization: 'This domain is reserved by another organization.',
  order_in_progress: 'You already have an open order for this domain.',
};

// Extra machine codes the create endpoint can return per-name (on top of the conflict codes above).
const EXTRA_ITEM_CODE_COPY: Record<string, string> = {
  tld_not_offered: "We don't offer transfers for this domain's extension.",
};

/**
 * Friendly copy for a transferability `conflict` code OR a create-time per-name item code. Falls
 * back to the raw value for anything unmapped — the backend's per-name `auth_code` errors are
 * already human-readable sentences, so surfacing them verbatim is correct.
 */
export function conflictCopy(code: string): string {
  const lookup: Record<string, string> = { ...CONFLICT_COPY, ...EXTRA_ITEM_CODE_COPY };
  return lookup[code] ?? code;
}
